import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Role } from "../../../../Api";
import { theme } from "../../../../theme";
import { userYears } from "../../../../utils/constants";
import ConfirmationModal from "../../../../Containers/ConfirmationModal";
import CustomEditor from "../../../../CustomComponents/CustomEditor";

type AnnouncementFormFieldsProps = {
	form: any;
	defaultValues: any;
	recipientRoleValue?: Role;
	facultyOptions: React.ReactNode[];
	departmentOptions: React.ReactNode[];
	studyProgramOptions: React.ReactNode[];
	companyOptions: React.ReactNode[];
	setFacultySearch: (v: string) => void;
	setDepartmentSearch: (v: string) => void;
	setStudyProgramSearch: (v: string) => void;
	setCompanySearch: (v: string) => void;
	cancelModalVisibility: boolean;
	saveModalVisibility: boolean;
	setCancelModalVisibility: (v: boolean) => void;
	setSaveModalVisibility: (v: boolean) => void;
	handleCancel: () => void;
	handleSave: () => void;
	spinning: boolean;
	isLoadingCompanies: boolean;
};

const { Option } = Select;

export const AnnouncementFormFields = (props: AnnouncementFormFieldsProps) => {
	const { t } = useTranslation();

	useEffect(() => {
		if (props.defaultValues.description) {
			props.form.setFieldsValue({ description: props.defaultValues.description });
		}
	}, [props.defaultValues.description, props.form]);

	return (
		<div className="container mx-auto px-4 py-10 flex flex-col gap-8">
			<Form form={props.form} layout={theme.layout}>
				<Form.Item
					required
					name="title"
					label={t("announcements.form.title") + ":"}
					rules={[
						{
							required: true,
							message: t("usersText.requiredField", {
								field: t("announcements.form.title"),
							}),
						},
						{
							max: 500,
							message:
								t("usersText.maxFieldLength1") +
								"500" +
								t("usersText.maxFieldLength2"),
						},
					]}
					initialValue={props.defaultValues.title}
				>
					<Input
						placeholder={t("announcements.form.title")}
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					required
					name="description"
					label={t("announcements.form.description") + ":"}
					rules={[
						{
							required: true,
							message: t("usersText.requiredField", {
								field: t("announcements.form.description"),
							}),
						},
					]}
				>
					<CustomEditor
						onEditorChange={(content: string) => {
							props.form.setFieldsValue({ description: content });
						}}
					/>
				</Form.Item>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							required
							name="validFrom"
							label={t("announcements.form.validFrom") + ":"}
							rules={[
								{
									required: true,
									message: t("usersText.requiredField", {
										field: t("announcements.form.validFrom"),
									}),
								},
							]}
							initialValue={props.defaultValues.validFrom}
						>
							<DatePicker
								className="w-full"
								format="DD.MM.YYYY"
								placeholder={t("announcements.form.validFrom")}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							required
							name="validTo"
							label={t("announcements.form.validTo") + ":"}
							rules={[
								{
									required: true,
									message: t("usersText.requiredField", {
										field: t("announcements.form.validTo"),
									}),
								},
							]}
							initialValue={props.defaultValues.validTo}
						>
							<DatePicker
								className="w-full"
								format="DD.MM.YYYY"
								placeholder={t("announcements.form.validTo")}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					required
					name="recipientRole"
					label={t("announcements.form.recipientRole") + ":"}
					rules={[
						{
							required: true,
							message: t("usersText.requiredField", {
								field: t("announcements.form.recipientRole"),
							}),
						},
					]}
					initialValue={props.defaultValues.recipientRole}
				>
					<Select
						allowClear
						placeholder={t("announcements.form.recipientRole")}
						style={{ width: "100%", textAlign: "left" }}
					>
						<Option value={Role.Student}>
							{t("announcements.targets.students")}
						</Option>
						<Option value={Role.Professor}>
							{t("announcements.targets.professors")}
						</Option>
						<Option value={Role.Company}>
							{t("announcements.targets.companies")}
						</Option>
					</Select>
				</Form.Item>

				{(props.recipientRoleValue === Role.Professor ||
					props.recipientRoleValue === Role.Student) && (
					<Form.Item
						name="faculties"
						label={t("announcements.form.faculties") + ":"}
					>
						<Select
							mode="multiple"
							maxTagCount="responsive"
							allowClear
							filterOption={false}
							onSearch={props.setFacultySearch}
							style={{ width: "100%", textAlign: "left" }}
							placeholder={t("announcements.form.faculties")}
							notFoundContent={t("emails.publishEmailForm.notFoundContent")}
						>
							{props.facultyOptions}
						</Select>
					</Form.Item>
				)}

				{props.recipientRoleValue === Role.Professor && (
					<Form.Item
						name="departments"
						label={t("announcements.form.departments") + ":"}
					>
						<Select
							mode="multiple"
							maxTagCount="responsive"
							allowClear
							filterOption={false}
							onSearch={props.setDepartmentSearch}
							style={{ width: "100%", textAlign: "left" }}
							placeholder={t("announcements.form.departments")}
							notFoundContent={t("emails.publishEmailForm.notFoundContent")}
						>
							{props.departmentOptions}
						</Select>
					</Form.Item>
				)}

				{props.recipientRoleValue === Role.Student && (
					<>
						<Form.Item
							name="studyPrograms"
							label={t("announcements.form.studyPrograms") + ":"}
						>
							<Select
								mode="multiple"
								maxTagCount="responsive"
								allowClear
								filterOption={false}
								onSearch={props.setStudyProgramSearch}
								style={{ width: "100%", textAlign: "left" }}
								placeholder={t("announcements.form.studyPrograms")}
								notFoundContent={t("emails.publishEmailForm.notFoundContent")}
							>
								{props.studyProgramOptions}
							</Select>
						</Form.Item>
						<Form.Item name="years" label={t("announcements.form.years") + ":"}>
							<Select
								mode="multiple"
								maxTagCount="responsive"
								allowClear
								style={{ width: "100%", textAlign: "left" }}
								placeholder={t("announcements.form.years")}
							>
								{userYears.map((year) => (
									<Option key={year} value={year}>
										{t("filtering." + year)}
									</Option>
								))}
							</Select>
						</Form.Item>
					</>
				)}

				{props.recipientRoleValue === Role.Company && (
					<Form.Item
						name="targetCompanyIds"
						label={t("announcements.form.targetCompanies") + ":"}
					>
						<Select
							mode="multiple"
							maxTagCount="responsive"
							allowClear
							filterOption={false}
							onSearch={props.setCompanySearch}
							style={{ width: "100%", textAlign: "left" }}
							placeholder={t("announcements.form.targetCompanies")}
							notFoundContent={
								props.isLoadingCompanies ? (
									<span>{t("generic.loading")}</span>
								) : null
							}
						>
							{props.companyOptions}
						</Select>
					</Form.Item>
				)}

				<div className="flex justify-end items-center gap-4 pt-4">
					<Button onClick={() => props.setCancelModalVisibility(true)}>
						{t("announcements.form.cancel")}
					</Button>
					<ConfirmationModal
						modalText={t("announcements.form.cancel") + "?"}
						handleFunction={props.handleCancel}
						modalVisibility={props.cancelModalVisibility}
						changeModalVisibility={() => props.setCancelModalVisibility(false)}
						title=""
						spinning={props.spinning}
					/>
					<Button
						type="primary"
						onClick={() => props.setSaveModalVisibility(true)}
					>
						{t("announcements.form.save")}
					</Button>
					<ConfirmationModal
						modalText={t("announcements.form.save") + "?"}
						handleFunction={props.handleSave}
						modalVisibility={props.saveModalVisibility}
						changeModalVisibility={() => props.setSaveModalVisibility(false)}
						title=""
						spinning={props.spinning}
					/>
				</div>
			</Form>
		</div>
	);
}